import type { ReactNode } from "react";
import { useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import { ReportFrame, reportMeta } from "./Frame";
import {
  Money,
  ModelledFootnote,
  Footnote,
  ShareBar,
  SubHeading,
  plural,
  shortDate,
} from "../kit";
import { cn } from "@/lib/utils";

/**
 * Report: vendor spending.
 *
 * Owner-only, and the query enforces that — `requireOwner`, not this component.
 * Nothing here hides a field; if a non-owner ever renders this, the query
 * throws and the frame shows its error state.
 *
 * Every figure on this screen is Nexora's cost, and most of the catalogue's
 * cost is modelled rather than real. That is why `ModelledFootnote` is the first
 * thing in the body rather than something at the bottom of the screen: the
 * caveat has to be visible in the same glance as the number, including from the
 * back of a room.
 *
 * The figures are `<dl>` pairs — the registry's own card-detail idiom, a label
 * in `text-muted-foreground` against a right-aligned figure. The per-vendor
 * blocks are rows of one list separated by rules, not eleven cards inside the
 * report's card.
 */

const pct = (share: number): string => `${Math.round(share * 100)}%`;

/** One label/figure pair. Twenty of these were open-coding the same flex row. */
function Line({
  label,
  children,
  tone = "neutral",
}: {
  label: string;
  children: ReactNode;
  tone?: "neutral" | "red";
}) {
  return (
    <div
      className={cn(
        "flex items-baseline justify-between gap-3",
        tone === "red" && "text-red-800",
      )}
    >
      <dt
        className={cn("min-w-0 truncate", tone !== "red" && "text-muted-foreground")}
      >
        {label}
      </dt>
      <dd className="num shrink-0 font-medium">{children}</dd>
    </div>
  );
}

export function VendorSpending() {
  const data = useQuery(api.reports.vendorSpending.data);

  return (
    <ReportFrame
      {...reportMeta("vendor-spending")}
      question="Where is the money going, and to whom?"
      data={data}
      count={data?.vendors.length}
      empty={{
        title: "Nothing bought from a vendor yet",
        body: "Spend appears here as soon as the first dispatch leaves the warehouse or a line is ordered from a vendor.",
      }}
      footnote={data?.attributionNote}
    >
      {data ? (
        <div className="space-y-4">
          <ModelledFootnote
            modelled={data.modelled.modelledItemsFeeding}
            total={data.modelled.itemsFeeding}
            bandMin={data.modelled.bandMin}
            bandMax={data.modelled.bandMax}
          />

          <div>
            <div className="num text-3xl font-semibold tracking-tight">
              <Money cents={data.totals.costCents} />
            </div>
            <div className="mt-0.5 text-sm text-muted-foreground">
              Total at Nexora cost · {data.totals.vendorCount}{" "}
              {plural(data.totals.vendorCount, "vendor")} · {data.totals.lines}{" "}
              lines
            </div>

            {data.modelled.modelledCostCents > 0 ? (
              <Footnote tone="caution" className="mt-2">
                <Money cents={data.modelled.modelledCostCents} /> of that (
                {pct(data.modelled.valueShare)}) comes from{" "}
                {data.modelled.modelledItemsFeeding} of{" "}
                {data.modelled.itemsFeeding} items whose cost is modelled, not a
                real supplier figure.
              </Footnote>
            ) : null}

            <dl className="mt-3 space-y-1.5 text-sm">
              <Line label="Dispatched from warehouse">
                <Money cents={data.totals.dispatchedCostCents} /> ·{" "}
                {data.totals.dispatchedLines} lines
              </Line>
              <Line label="On order with vendor">
                <Money cents={data.totals.onOrderCostCents} /> ·{" "}
                {data.totals.onOrderLines} lines
              </Line>
              {data.totals.backOrderedLines > 0 ? (
                <Line label="Back ordered (not in the total)">
                  <Money cents={data.totals.backOrderedCostCents} /> ·{" "}
                  {data.totals.backOrderedLines} lines
                </Line>
              ) : null}
              {data.period.firstDispatchAt !== null &&
              data.period.lastDispatchAt !== null ? (
                <Line label="Dispatch period">
                  {shortDate(data.period.firstDispatchAt)} –{" "}
                  {shortDate(data.period.lastDispatchAt)}
                </Line>
              ) : null}
              {data.totals.unattributedLines > 0 ? (
                <Line label="Lines with no item on file" tone="red">
                  {data.totals.unattributedLines}
                </Line>
              ) : null}
            </dl>
          </div>

          <div className="border-t pt-4">
            <SubHeading as="h4">By vendor</SubHeading>
            <ul className="divide-y">
              {data.vendors.map((vendor) => (
                /* `data-vendor` selects one vendor's block. */
                <li
                  key={vendor.vendor}
                  data-vendor={vendor.vendor}
                  className="py-4 last:pb-0"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">
                        {vendor.vendor}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {vendor.totalLines} lines · {vendor.totalUnits} units ·{" "}
                        {vendor.catalogueItems} catalogue items
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <div className="num text-base font-semibold">
                        <Money cents={vendor.totalCostCents} />
                      </div>
                      <div className="num text-xs text-muted-foreground">
                        {pct(vendor.valueShare)} of spend
                      </div>
                    </div>
                  </div>

                  {/* Share of total value. A bar rather than a chart: it
                      survives 390px, one vendor, and a projector. */}
                  <ShareBar
                    className="mt-2"
                    pct={Math.round(vendor.valueShare * 100)}
                    minPct={2}
                    role="img"
                    label={`${vendor.vendor}: ${pct(vendor.valueShare)} of total spend`}
                  />

                  <dl className="mt-3 space-y-1 text-xs">
                    <Line label="Dispatched">
                      <Money cents={vendor.dispatched.costCents} /> ·{" "}
                      {vendor.dispatched.lines} lines
                    </Line>
                    <Line label="On order">
                      <Money cents={vendor.onOrder.costCents} /> ·{" "}
                      {vendor.onOrder.lines} lines
                    </Line>
                    {vendor.backOrdered.lines > 0 ? (
                      <Line label="Back ordered">
                        <Money cents={vendor.backOrdered.costCents} /> ·{" "}
                        {vendor.backOrdered.lines} lines
                      </Line>
                    ) : null}
                    <Line label="Share of lines">{pct(vendor.lineShare)}</Line>
                    {vendor.modelledItemsFeeding > 0 ? (
                      <Line label="Modelled cost">
                        {vendor.modelledItemsFeeding} of {vendor.itemsFeeding}{" "}
                        items · <Money cents={vendor.modelledCostCents} />
                      </Line>
                    ) : null}
                    {vendor.zeroCostLines > 0 ? (
                      <Line label="Lines at no cost (vendor promo)">
                        {vendor.zeroCostLines}
                      </Line>
                    ) : null}
                  </dl>

                  {vendor.manufacturers.length > 1 ? (
                    <div className="mt-3 border-t pt-2">
                      <p className="text-xs font-medium text-muted-foreground">
                        Top brands within {vendor.vendor}
                      </p>
                      <dl className="mt-1 space-y-1 text-xs">
                        {vendor.manufacturers.map((m) => (
                          <Line key={m.name} label={m.name}>
                            <Money cents={m.costCents} /> · {m.lines} lines
                          </Line>
                        ))}
                      </dl>
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </ReportFrame>
  );
}
